import { AxiosResponse } from 'axios';
import AbstractHttpService from '@services/abstractHttpService';

import { ACTION_REQUIRED_COUNTS_UPDATED_EVENT } from '@const/events';
import urls from '@const/urls';
import { needResetParams } from '@const/consts';
import { Nullable } from '@app/types';
import { IOrganisationSignatureInlineRequest } from '@store/modules/user/types';

export enum CloudDigitalSigningProvider {
  FPT = 'FPT',
}

export enum CloudDigitalSigningResultStatus {
  SIGNED = 'SIGNED',
  SIGNING_FAILED = 'SIGNING_FAILED',
  CERIFICATE_DO_NOT_MATCH = 'CERIFICATE_DO_NOT_MATCH',
  CERTIFICATE_DO_NOT_MATCH = 'CERTIFICATE_DO_NOT_MATCH',
}

type ICloudOrganisationSignatureRequest = Omit<IOrganisationSignatureInlineRequest, 'fullName'>;

export interface ICloudDigitalSigningRequest {
  provider: CloudDigitalSigningProvider;
  signer?: Nullable<{
    fullName: string;
    initials: Nullable<string>;
    position: Nullable<string>;
  }>;
  userOrganisationSignatureOguid?: Nullable<string>;
  organisationSignature?: Nullable<ICloudOrganisationSignatureRequest>;
  providerUserIdentity?: Nullable<string>;
  passcode?: Nullable<string>;
  isIgnoreCertificateValidation?: boolean;
}

export interface ICloudDigitalSigningResponse {
  provider: CloudDigitalSigningProvider;
  status: CloudDigitalSigningResultStatus;
  failure: Nullable<{
    message: string;
  }>;
}

export interface IGraphicalSigningRequest {
  signer?: Nullable<{
    fullName: string;
    initials?: Nullable<string>;
    position?: Nullable<string>;
  }>;
  electronicSignatureBase64?: Nullable<string>;
  marks?: Nullable<Record<string, boolean | string>>;
}

class TasksService extends AbstractHttpService {
  private readonly url = urls.tasks;

  async postTaskComplete(
    taskOguid: string,
    comment: Nullable<string>
  ): Promise<AxiosResponse<void>> {
    const url = this.url.complete.replace('{{ taskOguid }}', taskOguid);
    const response = await this._http.post(url, { comment: comment || null });

    window.dispatchEvent(new Event(ACTION_REQUIRED_COUNTS_UPDATED_EVENT));

    return response;
  }

  async postTaskDecline(
    oguid: string,
    comment: Nullable<string>
  ): Promise<AxiosResponse> {
    const url = this.url.decline.replace('{{ taskOguid }}', oguid);
    const response = await this._http.post(url, { comment: comment || null });

    window.dispatchEvent(new Event(ACTION_REQUIRED_COUNTS_UPDATED_EVENT));

    return response;
  }

  async postTasksMarkIsRead(
    taskOguids: string[],
    isRead: boolean
  ): Promise<AxiosResponse> {
    const url = this.url.markIsRead;

    return await this._http.post(url, { taskOguids, isRead });
  }

  async postCloudDigitalSigning(
    taskOguid: string,
    data: ICloudDigitalSigningRequest
  ): Promise<AxiosResponse<ICloudDigitalSigningResponse>> {
    const url = this.url.cloudDigitalSigning.replace('{{ taskOguid }}', taskOguid);
    const response = await this._http.post(url, data, { params: needResetParams });

    if (response.data.status === CloudDigitalSigningResultStatus.SIGNED) {
      window.dispatchEvent(new Event(ACTION_REQUIRED_COUNTS_UPDATED_EVENT));
    }

    return response;
  }

  async postGraphicalSigning(
    taskOguid: string,
    data: IGraphicalSigningRequest
  ): Promise<AxiosResponse<void>> {
    const url = this.url.graphicalSigning.replace('{{ taskOguid }}', taskOguid);
    const response = await this._http.post(url, data, { params: needResetParams });

    window.dispatchEvent(new Event(ACTION_REQUIRED_COUNTS_UPDATED_EVENT));

    return response;
  }
}

export default new TasksService();
